<div class="gsr">
  <!-- Header -->
  <header class="gsr-header">
    <div>
      <h2 class="gsr-title">General Sales Report</h2>
      <p class="gsr-subtitle">
        {{ report.meta.startDate | date: 'MMM d, y' }} &ndash;
        {{ report.meta.endDate | date: 'MMM d, y' }} &middot; {{ report.meta.locationLabel }}
        @if (report.meta.compareEnabled && report.meta.compareStartDate) {
          &middot; Compared with {{ report.meta.compareStartDate | date: 'MMM d' }} &ndash;
          {{ report.meta.compareEndDate | date: 'MMM d, y' }}
        }
      </p>
    </div>
    <div class="gsr-exports">
      <label
        class="gsr-export-option"
        title="Add the list of cancelled orders behind the Voids count to the exported file"
      >
        <input
          type="checkbox"
          [checked]="includeCancelledOrdersInExport"
          (change)="includeCancelledOrdersInExportChange.emit($any($event.target).checked)"
        />
        Include cancelled orders
      </label>
      <button
        type="button"
        class="gsr-export-btn"
        [disabled]="exportDisabled || exportingCsv"
        (click)="exportCsv.emit()"
        title="Raw data for analysis — plain text, no formatting"
      >
        {{ exportingCsv ? 'Preparing…' : 'Export CSV' }}
      </button>
      <button
        type="button"
        class="gsr-export-btn"
        [disabled]="exportDisabled || exportingXlsx"
        (click)="exportXlsx.emit()"
        title="Formatted spreadsheet — bold headers, currency columns, one sheet per section"
      >
        {{ exportingXlsx ? 'Preparing…' : 'Export Excel' }}
      </button>
      <button
        type="button"
        class="gsr-export-btn"
        [disabled]="exportDisabled || exportingPdf"
        (click)="exportPdf.emit()"
      >
        {{ exportingPdf ? 'Preparing…' : 'Export PDF' }}
      </button>
    </div>
  </header>

  <!-- KPI summary -->
  <section class="gsr-section" aria-labelledby="gsr-summary-title">
    <h3 id="gsr-summary-title" class="gsr-section__title">Summary</h3>
    <div class="gsr-kpis">
      @for (kpi of report.kpis; track trackByKpi($index, kpi)) {
        <div class="gsr-kpi" [class.gsr-kpi--unavailable]="!kpi.available">
          <span class="gsr-kpi__label">
            {{ kpi.label }}
            <app-info-tip [label]="kpi.label" [text]="tips.kpi[kpi.key]"></app-info-tip>
          </span>
          <span class="gsr-kpi__value">{{ kpi.value }}</span>
          @if (hasComparison(kpi)) {
            <span
              class="gsr-kpi__delta"
              [class.gsr-kpi__delta--up]="deltaIsPositive(kpi)"
              [class.gsr-kpi__delta--down]="!deltaIsPositive(kpi)"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                @if (deltaIsUp(kpi)) {
                  <path
                    d="M6 9.5v-7M2.75 5.75L6 2.5l3.25 3.25"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                } @else {
                  <path
                    d="M6 2.5v7M2.75 6.25L6 9.5l3.25-3.25"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                }
              </svg>
              {{ deltaLabel(kpi) }}
            </span>
          }
          @if (kpi.hint) {
            <span class="gsr-kpi__hint">{{ kpi.hint }}</span>
          }
        </div>
      }
    </div>
  </section>

  <div class="gsr-divider"></div>

  <!-- Volume & Revenue -->
  <section class="gsr-section" aria-labelledby="gsr-volume-title">
    <div class="gsr-section__head">
      <h3 id="gsr-volume-title" class="gsr-section__title">Volume &amp; Revenue</h3>
      <span class="gsr-section__meta">Grouped by location business timezone</span>
    </div>

    <!-- PRD §27 places these figures inside Volume & Revenue, not only in the
           summary at the top of the report. -->
    <div class="gsr-figures">
      <div class="gsr-figure">
        <span class="gsr-figure__label">
          Total Cars Washed
          <app-info-tip
            label="Total Cars Washed"
            [text]="tips.volume.totalCarsWashed"
          ></app-info-tip>
        </span>
        <span class="gsr-figure__value">{{ report.volume.totalCarsWashed | number }}</span>
      </div>
      <div class="gsr-figure">
        <span class="gsr-figure__label">
          Total Packages Purchased
          <app-info-tip
            label="Total Packages Purchased"
            [text]="tips.volume.totalPackagesPurchased"
          ></app-info-tip>
        </span>
        <span class="gsr-figure__value">{{
          report.volume.totalPackagesPurchased ?? 0 | number
        }}</span>
        <span class="gsr-figure__note">paid orders of any type</span>
      </div>
      <div class="gsr-figure">
        <span class="gsr-figure__label">
          Average Cars / Day
          <app-info-tip
            label="Average Cars / Day"
            [text]="tips.volume.avgCarsPerDay"
          ></app-info-tip>
        </span>
        <span class="gsr-figure__value">{{ report.volume.avgCarsPerDay | number }}</span>
        <span class="gsr-figure__note">over {{ report.meta.calendarDays }} calendar days</span>
      </div>
      <div class="gsr-figure">
        <span class="gsr-figure__label">
          Gross Sales
          <app-info-tip label="Gross Sales" [text]="tips.volume.grossSales"></app-info-tip>
        </span>
        <span class="gsr-figure__value">
          {{ report.volume.grossSales | currency: 'USD' : 'symbol' : '1.0-0' }}
        </span>
        <span class="gsr-figure__note">every paid order, before discounts</span>
      </div>
      <div class="gsr-figure">
        <span class="gsr-figure__label">
          Net Sales
          <app-info-tip label="Net Sales" [text]="tips.volume.netSales"></app-info-tip>
        </span>
        <span class="gsr-figure__value">
          {{ report.volume.netSales | currency: 'USD' : 'symbol' : '1.0-0' }}
        </span>
        <span class="gsr-figure__note">after discounts &middot; matches the dashboard</span>
      </div>
      <div class="gsr-figure gsr-figure--lead">
        <span class="gsr-figure__label">
          Average Ticket
          <app-info-tip label="Average Ticket" [text]="tips.volume.averageTicket"></app-info-tip>
        </span>
        <span class="gsr-figure__value">
          {{
            report.volume.averageTicket === null
              ? 'N/A'
              : (report.volume.averageTicket | currency: 'USD')
          }}
        </span>
        <span class="gsr-figure__note">net sales &divide; cars washed</span>
      </div>
    </div>

    <div class="gsr-panel">
      <div class="gsr-panel__head">
        <span class="gsr-panel__title">
          {{ trendBucketLabel }} sales
          <app-info-tip label="Sales trend" [text]="tips.volume.dailySales"></app-info-tip>
        </span>
        <div class="gsr-legend">
          <span class="gsr-legend__item">
            <span class="gsr-swatch" [style.background]="seriesViolet"></span>Gross sales
          </span>
          <span class="gsr-legend__item">
            <span class="gsr-swatch" [style.background]="seriesGreen"></span>Net sales
          </span>
          @if (previousPoints) {
            <span class="gsr-legend__item">
              <span class="gsr-swatch gsr-swatch--line" [style.background]="seriesMuted"></span>
              Previous period
            </span>
          }
        </div>
      </div>

      <svg
        class="gsr-chart"
        viewBox="0 0 1060 260"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Daily gross and net sales across the selected period"
      >
        @for (tick of salesYTicks; track tick.value) {
          <line class="gsr-grid" x1="56" [attr.y1]="tick.y" x2="1036" [attr.y2]="tick.y" />
          <text class="gsr-axis" x="46" [attr.y]="tick.y + 4" text-anchor="end">
            {{ tick.label }}
          </text>
        }
        @if (netArea) {
          <path [attr.d]="netArea" [attr.fill]="seriesGreen" fill-opacity="0.07" />
        }
        @if (previousPoints) {
          <polyline
            [attr.points]="previousPoints"
            fill="none"
            [attr.stroke]="seriesMuted"
            stroke-width="2"
            stroke-dasharray="4 4"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        }
        <polyline
          [attr.points]="grossPoints"
          fill="none"
          [attr.stroke]="seriesViolet"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <polyline
          [attr.points]="netPoints"
          fill="none"
          [attr.stroke]="seriesGreen"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        @for (tick of salesXTicks; track tick.x) {
          <text class="gsr-axis" [attr.x]="tick.x" y="240" text-anchor="middle">
            {{ tick.label }}
          </text>
        }
        @for (band of salesBands; track band.cx; let i = $index) {
          <rect
            [attr.x]="band.x"
            y="20"
            [attr.width]="band.width"
            height="200"
            fill="transparent"
            (mouseenter)="onChartHover('sales', i)"
            (mouseleave)="onChartLeave()"
          ></rect>
        }
        @if (activeBand('sales'); as band) {
          <line
            [attr.x1]="band.cx"
            y1="20"
            [attr.x2]="band.cx"
            y2="220"
            stroke="#A7ABB3"
            stroke-width="1"
          />
          <circle
            [attr.cx]="band.cx"
            [attr.cy]="band.cy"
            r="4"
            [attr.fill]="seriesViolet"
            stroke="#FFFFFF"
            stroke-width="2"
          />
          <g [attr.transform]="'translate(' + tooltipX(band, 180, 1036, 56) + ', 26)'">
            <rect
              width="180"
              [attr.height]="26 + band.lines.length * 16"
              rx="6"
              fill="#15172E"
              opacity="0.94"
            />
            <text x="12" y="18" fill="#FFFFFF" font-size="11" font-weight="600">
              {{ band.label }}
            </text>
            @for (line of band.lines; track line.text; let li = $index) {
              <text
                x="12"
                [attr.y]="36 + li * 16"
                [attr.fill]="line.color || '#D3D5D9'"
                font-size="11"
              >
                {{ line.text }}
              </text>
            }
          </g>
        }
      </svg>
    </div>

    <div class="gsr-grid-2">
      <div class="gsr-panel">
        <div class="gsr-panel__head">
          <span class="gsr-panel__title">
            Wash volume
            <app-info-tip label="Wash volume" [text]="tips.volume.washVolume"></app-info-tip>
          </span>
          <div class="gsr-legend">
            <span class="gsr-legend__item">
              <span class="gsr-swatch" [style.background]="seriesViolet"></span>Cars washed
            </span>
          </div>
        </div>
        <span class="gsr-section__meta">Cars per day</span>
        @if (carsPoints) {
          <svg
            class="gsr-chart"
            viewBox="0 0 520 190"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Cars washed per day across the selected period"
          >
            @for (tick of carsYTicks; track tick.value) {
              <line class="gsr-grid" x1="48" [attr.y1]="tick.y" x2="500" [attr.y2]="tick.y" />
              <text class="gsr-axis" x="40" [attr.y]="tick.y + 4" text-anchor="end">
                {{ tick.label }}
              </text>
            }
            <polyline
              [attr.points]="carsPoints"
              fill="none"
              [attr.stroke]="seriesGreen"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            @for (marker of carsMarkers; track marker.x) {
              <circle
                [attr.cx]="marker.x"
                [attr.cy]="marker.y"
                r="3"
                [attr.fill]="seriesGreen"
                stroke="#FFFFFF"
                stroke-width="1.5"
              />
            }
            @for (tick of carsXTicks; track tick.x) {
              <text class="gsr-axis" [attr.x]="tick.x" y="180" text-anchor="middle">
                {{ tick.label }}
              </text>
            }
            @for (band of carsBands; track band.cx; let i = $index) {
              <rect
                [attr.x]="band.x"
                y="20"
                [attr.width]="band.width"
                height="140"
                fill="transparent"
                (mouseenter)="onChartHover('cars', i)"
                (mouseleave)="onChartLeave()"
              ></rect>
            }
            @if (activeBand('cars'); as band) {
              <line
                [attr.x1]="band.cx"
                y1="20"
                [attr.x2]="band.cx"
                y2="160"
                stroke="#A7ABB3"
                stroke-width="1"
              />
              <circle
                [attr.cx]="band.cx"
                [attr.cy]="band.cy"
                r="4"
                [attr.fill]="seriesGreen"
                stroke="#FFFFFF"
                stroke-width="2"
              />
              <g [attr.transform]="'translate(' + tooltipX(band, 150, 500, 48) + ', 26)'">
                <rect
                  width="150"
                  [attr.height]="26 + band.lines.length * 16"
                  rx="6"
                  fill="#15172E"
                  opacity="0.94"
                />
                <text x="12" y="18" fill="#FFFFFF" font-size="11" font-weight="600">
                  {{ band.label }}
                </text>
                @for (line of band.lines; track line.text; let li = $index) {
                  <text
                    x="12"
                    [attr.y]="36 + li * 16"
                    [attr.fill]="line.color || '#D3D5D9'"
                    font-size="11"
                  >
                    {{ line.text }}
                  </text>
                }
              </g>
            }
          </svg>
        }
      </div>

      <div class="gsr-panel">
        <div class="gsr-panel__head">
          <span class="gsr-panel__title">
            Average ticket
            <app-info-tip
              label="Average ticket trend"
              [text]="tips.volume.averageTicketTrend"
            ></app-info-tip>
          </span>
          <div class="gsr-legend">
            <span class="gsr-legend__item">
              <span class="gsr-swatch" [style.background]="seriesGreen"></span>Net sales per car
            </span>
          </div>
        </div>
        <span class="gsr-section__meta">Buckets with no washes are omitted</span>
        @if (ticketPoints) {
          <svg
            class="gsr-chart"
            viewBox="0 0 520 190"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Average ticket across the selected period"
          >
            @for (tick of ticketYTicks; track tick.value) {
              <line class="gsr-grid" x1="48" [attr.y1]="tick.y" x2="500" [attr.y2]="tick.y" />
              <text class="gsr-axis" x="40" [attr.y]="tick.y + 4" text-anchor="end">
                {{ tick.label }}
              </text>
            }
            <polyline
              [attr.points]="ticketPoints"
              fill="none"
              [attr.stroke]="seriesAmber"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            @for (marker of ticketMarkers; track marker.x) {
              <circle
                [attr.cx]="marker.x"
                [attr.cy]="marker.y"
                r="3"
                [attr.fill]="seriesAmber"
                stroke="#FFFFFF"
                stroke-width="1.5"
              />
            }
            @for (tick of ticketXTicks; track tick.x) {
              <text class="gsr-axis" [attr.x]="tick.x" y="180" text-anchor="middle">
                {{ tick.label }}
              </text>
            }
            @for (band of ticketBands; track band.cx; let i = $index) {
              <rect
                [attr.x]="band.x"
                y="20"
                [attr.width]="band.width"
                height="140"
                fill="transparent"
                (mouseenter)="onChartHover('ticket', i)"
                (mouseleave)="onChartLeave()"
              ></rect>
            }
            @if (activeBand('ticket'); as band) {
              <line
                [attr.x1]="band.cx"
                y1="20"
                [attr.x2]="band.cx"
                y2="160"
                stroke="#A7ABB3"
                stroke-width="1"
              />
              <circle
                [attr.cx]="band.cx"
                [attr.cy]="band.cy"
                r="4"
                [attr.fill]="seriesAmber"
                stroke="#FFFFFF"
                stroke-width="2"
              />
              <g [attr.transform]="'translate(' + tooltipX(band, 150, 500, 48) + ', 26)'">
                <rect
                  width="150"
                  [attr.height]="26 + band.lines.length * 16"
                  rx="6"
                  fill="#15172E"
                  opacity="0.94"
                />
                <text x="12" y="18" fill="#FFFFFF" font-size="11" font-weight="600">
                  {{ band.label }}
                </text>
                @for (line of band.lines; track line.text; let li = $index) {
                  <text
                    x="12"
                    [attr.y]="36 + li * 16"
                    [attr.fill]="line.color || '#D3D5D9'"
                    font-size="11"
                  >
                    {{ line.text }}
                  </text>
                }
              </g>
            }
          </svg>
        }
      </div>
    </div>

    <div class="gsr-grid-2">
      <div class="gsr-panel">
        <div class="gsr-panel__head">
          <span class="gsr-panel__title">
            Gross to net
            <app-info-tip label="Gross to net" [text]="tips.volume.grossToNet"></app-info-tip>
          </span>
          <div class="gsr-legend">
            <span class="gsr-legend__item">
              <span class="gsr-swatch" [style.background]="seriesGreen"></span>Kept (net)
            </span>
            <span class="gsr-legend__item">
              <span class="gsr-swatch" [style.background]="seriesAmber"></span>Given away
            </span>
          </div>
        </div>
        <div class="gsr-rows">
          <div class="gsr-row">
            <span class="gsr-row__label">
              Gross sales
              <app-info-tip label="Gross sales" [text]="tips.volume.grossSales"></app-info-tip>
            </span>
            <span class="gsr-row__value">{{
              report.volume.grossSales | currency: 'USD' : 'symbol' : '1.0-0'
            }}</span>
          </div>
          <div class="gsr-row">
            <span class="gsr-row__label">
              Net sales
              <app-info-tip label="Net sales" [text]="tips.volume.netSales"></app-info-tip>
            </span>
            <span class="gsr-row__value">{{
              report.volume.netSales | currency: 'USD' : 'symbol' : '1.0-0'
            }}</span>
          </div>
          <div class="gsr-rule"></div>
          <div class="gsr-row">
            <span class="gsr-row__label gsr-row__label--strong">
              Difference
              <app-info-tip label="Difference" [text]="tips.volume.difference"></app-info-tip>
            </span>
            <span class="gsr-row__value gsr-row__value--strong">
              {{ report.volume.grossToNetGap | currency: 'USD' : 'symbol' : '1.0-0' }}
            </span>
          </div>
          <div class="gsr-stack">
            <span
              class="gsr-stack__seg gsr-tip"
              tabindex="0"
              [style.flex]="netSharePct"
              [style.background]="seriesGreen"
              [attr.data-tip]="
                'Kept · ' +
                (report.volume.netSales | currency: 'USD' : 'symbol' : '1.0-0') +
                ' · ' +
                (netSharePct | number: '1.1-1') +
                '% of gross'
              "
              [attr.aria-label]="
                'Net sales kept, ' + (netSharePct | number: '1.1-1') + ' percent of gross'
              "
            ></span>
            <span
              class="gsr-stack__seg gsr-tip"
              tabindex="0"
              [style.flex]="deductionSharePct"
              [style.background]="seriesAmber"
              [attr.data-tip]="
                'Given away · ' +
                (report.volume.grossToNetGap | currency: 'USD' : 'symbol' : '1.0-0') +
                ' · discounts'
              "
              [attr.aria-label]="
                'Deductions, ' + (deductionSharePct | number: '1.1-1') + ' percent of gross'
              "
            ></span>
          </div>
          <span class="gsr-note">
            Deductions are {{ deductionSharePct | number: '1.1-1' }}% of gross sales
          </span>
        </div>
      </div>

      <div class="gsr-panel">
        <div class="gsr-panel__head">
          <span class="gsr-panel__title">
            Revenue breakdown
            <app-info-tip
              label="Revenue breakdown"
              [text]="tips.volume.revenueBreakdown"
            ></app-info-tip>
          </span>
          <div class="gsr-legend">
            @for (segment of revenueSegments; track segment.label) {
              <span class="gsr-legend__item">
                <span class="gsr-swatch" [style.background]="segment.color"></span>
                {{ segment.label }}
              </span>
            }
          </div>
        </div>
        <div class="gsr-stack">
          @for (segment of revenueSegments; track segment.label) {
            <span
              class="gsr-stack__seg gsr-tip"
              aria-hidden="true"
              [style.flex]="segment.sharePct"
              [style.background]="segment.color"
              [attr.data-tip]="
                segment.label +
                ' · ' +
                (segment.revenue | currency: 'USD' : 'symbol' : '1.0-0') +
                ' · ' +
                (segment.sharePct | number: '1.0-0') +
                '% of revenue'
              "
            ></span>
          }
        </div>
        <div class="gsr-table-wrap">
          <table class="gsr-table gsr-table--compact">
            <thead>
              <tr>
                <th scope="col">
                  Revenue Category
                  <app-info-tip
                    label="Revenue Category"
                    [text]="tips.volume.revenueBreakdown"
                  ></app-info-tip>
                </th>
                <th scope="col" class="gsr-num">
                  Units
                  <app-info-tip label="Units" [text]="tips.volume.breakdownUnits"></app-info-tip>
                </th>
                <th scope="col" class="gsr-num">
                  Revenue
                  <app-info-tip
                    label="Revenue"
                    [text]="tips.volume.breakdownRevenue"
                  ></app-info-tip>
                </th>
                <th scope="col" class="gsr-num">
                  % of Revenue
                  <app-info-tip
                    label="% of Revenue"
                    [text]="tips.volume.breakdownShare"
                  ></app-info-tip>
                </th>
              </tr>
            </thead>
            <tbody>
              @for (row of report.volume.revenueBreakdown; track row.category) {
                <tr>
                  <td class="gsr-strong">
                    <span
                      class="gsr-swatch"
                      [style.background]="categoryColor(row.category)"
                    ></span>
                    {{ row.label }}
                    <app-info-tip
                      [label]="row.label"
                      [text]="tips.revenueCategory[row.category]"
                    ></app-info-tip>
                  </td>
                  <td class="gsr-num">{{ row.units | number }}</td>
                  <td class="gsr-num">{{ row.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}</td>
                  <td class="gsr-num">{{ row.sharePct | number: '1.0-0' }}%</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>

  <div class="gsr-divider"></div>

  <!-- Package mix -->
  <section class="gsr-section" aria-labelledby="gsr-package-title">
    <div class="gsr-section__head">
      <h3 id="gsr-package-title" class="gsr-section__title">Wash Package Mix</h3>
      <span class="gsr-section__meta">
        Share of wash revenue &middot;
        {{ report.packageMixTotals.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}
      </span>
    </div>

    <div class="gsr-split">
      <div class="gsr-panel gsr-panel--bars">
        @for (row of report.packageMix; track trackByPackage($index, row)) {
          <div class="gsr-bar">
            <div class="gsr-bar__head">
              <span class="gsr-bar__label">
                <span
                  class="gsr-swatch"
                  [style.background]="packageColor($index, row.unmapped, row.prepaid)"
                ></span>
                {{ row.packageName }}
              </span>
              <span class="gsr-bar__value">{{ row.revenueSharePct | number: '1.1-1' }}%</span>
            </div>
            <div
              class="gsr-bar__track gsr-tip"
              aria-hidden="true"
              [attr.data-tip]="
                row.packageName +
                ' · ' +
                (row.units | number) +
                ' washes · ' +
                (row.revenue | currency: 'USD' : 'symbol' : '1.0-0') +
                ' · ' +
                (row.washSharePct | number: '1.1-1') +
                '% of washes'
              "
            >
              <div
                class="gsr-bar__fill"
                [style.width.%]="row.revenueSharePct"
                [style.background]="packageColor($index, row.unmapped, row.prepaid)"
              ></div>
            </div>
            @if (row.unmapped) {
              <span class="gsr-note">
                {{ row.units | number }} washes have no package on the order &mdash; shown, never
                silently excluded.
              </span>
            }
            @if (row.prepaid) {
              <span class="gsr-note">
                {{ row.units | number }} washes redeemed from a prepaid book &mdash; keyed by
                purchase, not by package.
              </span>
            }
          </div>
        }
      </div>

      <div class="gsr-table-wrap">
        <table class="gsr-table">
          <thead>
            <tr>
              <th scope="col">
                Wash Package
                <app-info-tip label="Wash Package" [text]="tips.packageMix.package"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Units
                <app-info-tip label="Units" [text]="tips.packageMix.units"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Revenue
                <app-info-tip label="Revenue" [text]="tips.packageMix.revenue"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                % of Washes
                <app-info-tip label="% of Washes" [text]="tips.packageMix.washShare"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                % of Revenue
                <app-info-tip
                  label="% of Revenue"
                  [text]="tips.packageMix.revenueShare"
                ></app-info-tip>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (row of report.packageMix; track trackByPackage($index, row)) {
              <tr>
                <td class="gsr-strong">{{ row.packageName }}</td>
                <td class="gsr-num">{{ row.units | number }}</td>
                <td class="gsr-num">{{ row.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}</td>
                <td class="gsr-num">{{ row.washSharePct | number: '1.1-1' }}%</td>
                <td class="gsr-num">{{ row.revenueSharePct | number: '1.1-1' }}%</td>
              </tr>
            }
            <tr class="gsr-total-row">
              <td>Total</td>
              <td class="gsr-num">{{ report.packageMixTotals.units | number }}</td>
              <td class="gsr-num">
                {{ report.packageMixTotals.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}
              </td>
              <td class="gsr-num">100%</td>
              <td class="gsr-num">100%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>

  <div class="gsr-divider"></div>

  <!-- Extras -->
  <section class="gsr-section" aria-labelledby="gsr-extras-title">
    <h3 id="gsr-extras-title" class="gsr-section__title">Extras &amp; Upsell Performance</h3>

    <div class="gsr-kpis gsr-kpis--three">
      <div class="gsr-kpi" [class.gsr-kpi--unavailable]="report.extras.attachRatePct === null">
        <span class="gsr-kpi__label">
          Extras Attach Rate
          <app-info-tip label="Extras Attach Rate" [text]="tips.extras.attachRate"></app-info-tip>
        </span>
        <span class="gsr-kpi__value">
          {{
            report.extras.attachRatePct === null
              ? 'N/A'
              : (report.extras.attachRatePct | number: '1.1-1') + '%'
          }}
        </span>
        <span class="gsr-kpi__hint">
          @if (report.extras.attachRatePct === null) {
            No eligible washes
          } @else {
            {{ report.extras.washesWithExtra | number }} of
            {{ report.extras.eligibleWashes | number }} eligible washes
          }
        </span>
      </div>
      <div
        class="gsr-kpi"
        [class.gsr-kpi--unavailable]="report.extras.revenuePerAttachedCar === null"
      >
        <span class="gsr-kpi__label">
          Revenue per Attached Car
          <app-info-tip
            label="Revenue per Attached Car"
            [text]="tips.extras.revenuePerAttachedCar"
          ></app-info-tip>
        </span>
        <span class="gsr-kpi__value">
          {{
            report.extras.revenuePerAttachedCar === null
              ? 'N/A'
              : (report.extras.revenuePerAttachedCar | currency: 'USD')
          }}
        </span>
        <span class="gsr-kpi__hint">Divided by attached cars, not all washes</span>
      </div>
      <div class="gsr-kpi">
        <span class="gsr-kpi__label">
          Total Extras Revenue
          <app-info-tip
            label="Total Extras Revenue"
            [text]="tips.extras.totalExtrasRevenue"
          ></app-info-tip>
        </span>
        <span class="gsr-kpi__value">
          {{ report.extras.totalExtrasRevenue | currency: 'USD' : 'symbol' : '1.0-0' }}
        </span>
        <span class="gsr-kpi__hint">Of net sales</span>
      </div>
    </div>

    <div class="gsr-table-wrap">
      <table class="gsr-table">
        <thead>
          <tr>
            <th scope="col">
              Extra
              <app-info-tip label="Extra" [text]="tips.extras.extra"></app-info-tip>
            </th>
            <th scope="col" class="gsr-num">
              Units Sold
              <app-info-tip label="Units Sold" [text]="tips.extras.unitsSold"></app-info-tip>
            </th>
            <th scope="col" class="gsr-num">
              Revenue
              <app-info-tip label="Revenue" [text]="tips.extras.revenue"></app-info-tip>
            </th>
            <th scope="col" class="gsr-num">
              Attach Rate
              <app-info-tip label="Attach Rate" [text]="tips.extras.rowAttachRate"></app-info-tip>
            </th>
            <th scope="col" class="gsr-num">
              Revenue / Attached Car
              <app-info-tip
                label="Revenue / Attached Car"
                [text]="tips.extras.rowRevenuePerAttachedCar"
              ></app-info-tip>
            </th>
          </tr>
        </thead>
        <tbody>
          @for (row of report.extras.rows; track trackByExtra($index, row)) {
            <tr>
              <td class="gsr-strong">{{ row.name }}</td>
              <td class="gsr-num">{{ row.units | number }}</td>
              <td class="gsr-num">{{ row.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}</td>
              <td class="gsr-num">{{ row.attachRatePct | number: '1.1-1' }}%</td>
              <td class="gsr-num">{{ row.revenuePerAttachedCar | currency: 'USD' }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <div class="gsr-divider"></div>

  <!-- Customer mix -->
  <section class="gsr-section" aria-labelledby="gsr-mix-title">
    <div class="gsr-section__head">
      <h3 id="gsr-mix-title" class="gsr-section__title">Customer / Wash Mix</h3>
      <span class="gsr-section__meta">Retail vs membership dependence</span>
    </div>

    <div class="gsr-panel">
      <!-- Two share bars, one per dimension. The gap between them is the point:
           membership can carry more traffic than revenue. Labels sit outside the
           bar so a 0% or 100% split cannot clip them. -->
      @for (dimension of ['washes', 'revenue']; track dimension) {
        <div class="gsr-sharebar">
          <span class="gsr-sharebar__title">
            Share of {{ dimension === 'washes' ? 'washes' : 'revenue' }}
            <app-info-tip
              [label]="'Share of ' + dimension"
              [text]="
                dimension === 'washes'
                  ? tips.customerMix.shareOfWashes
                  : tips.customerMix.shareOfRevenue
              "
            ></app-info-tip>
          </span>
          <!-- Driven off the rows, not a fixed pair, so a new customer type
               cannot be added to the table and silently missed by the chart. -->
          <div class="gsr-stack">
            @for (row of report.customerMix; track trackByCustomerType($index, row)) {
              @if (mixShare(row.type, $any(dimension)) > 0) {
                <span
                  class="gsr-stack__seg gsr-tip"
                  aria-hidden="true"
                  [style.flex]="mixShare(row.type, $any(dimension))"
                  [style.background]="customerColor(row.type)"
                  [attr.data-tip]="
                    row.label +
                    ' · ' +
                    (mixShare(row.type, $any(dimension)) | number: '1.1-1') +
                    '%'
                  "
                ></span>
              }
            }
          </div>
          <div class="gsr-sharebar__keys">
            @for (row of report.customerMix; track trackByCustomerType($index, row)) {
              <span class="gsr-legend__item">
                <span class="gsr-swatch" [style.background]="customerColor(row.type)"></span>
                {{ row.label }} {{ mixShare(row.type, $any(dimension)) | number: '1.1-1' }}%
              </span>
            }
          </div>
        </div>
      }

      <div class="gsr-table-wrap">
        <table class="gsr-table gsr-table--compact">
          <thead>
            <tr>
              <th scope="col">
                Customer Type
                <app-info-tip
                  label="Customer Type"
                  [text]="tips.customerMix.customerType"
                ></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Washes
                <app-info-tip label="Washes" [text]="tips.customerMix.washes"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                % of Washes
                <app-info-tip
                  label="% of Washes"
                  [text]="tips.customerMix.washShare"
                ></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Revenue
                <app-info-tip label="Revenue" [text]="tips.customerMix.revenue"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                % of Revenue
                <app-info-tip
                  label="% of Revenue"
                  [text]="tips.customerMix.revenueShare"
                ></app-info-tip>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (row of report.customerMix; track trackByCustomerType($index, row)) {
              <tr>
                <td class="gsr-strong">
                  <span class="gsr-swatch" [style.background]="customerColor(row.type)"></span>
                  {{ row.label }}
                  <app-info-tip
                    [label]="row.label"
                    [text]="tips.customerType[row.type]"
                  ></app-info-tip>
                </td>
                <td class="gsr-num">{{ row.washes | number }}</td>
                <td class="gsr-num">{{ row.washSharePct | number: '1.1-1' }}%</td>
                <td class="gsr-num">{{ row.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}</td>
                <td class="gsr-num">{{ row.revenueSharePct | number: '1.1-1' }}%</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      @if (dependenceLine) {
        <p class="gsr-reading">
          {{ dependenceLine }}
          @if (dependenceTrend) {
            <span class="gsr-reading__trend">{{ dependenceTrend }}</span>
          }
        </p>
      }
    </div>
  </section>

  <div class="gsr-divider"></div>

  <!-- Membership -->
  <section class="gsr-section" aria-labelledby="gsr-membership-title">
    <div class="gsr-section__head">
      <h3 id="gsr-membership-title" class="gsr-section__title">
        Membership / Unlimited Plan Performance
      </h3>
    </div>

    @if (!report.membership.available) {
      <div class="gsr-unavailable">
        <span class="gsr-unavailable__title">Membership data is unavailable for this period.</span>
        <span class="gsr-unavailable__body">
          Supporting data is incomplete, so these metrics are not shown. They are not zero.
        </span>
      </div>
    } @else {
      @if (report.membership.baseIsTenantWide && report.meta.locationCount > 0) {
        <!--
          subscriptions carry no location_id, so the base counts, churn and
          revenue billed are tenant-wide however the location filter is set.
          The CSV, XLSX and PDF exports all say so; on screen it was silent,
          which read as "these are the numbers for the location I picked".
        -->
        <p class="gsr-note gsr-note--scope" role="note">
          Membership counts cover every location; they are not narrowed by the location filter.
        </p>
      }

      <div class="gsr-panel gsr-base">
        <div class="gsr-base__item">
          <span class="gsr-kpi__label">
            Beginning Members
            <app-info-tip
              label="Beginning Members"
              [text]="tips.membership.beginning"
            ></app-info-tip>
          </span>
          <span class="gsr-base__value">{{ report.membership.base.beginning | number }}</span>
        </div>
        <span class="gsr-base__op" aria-hidden="true">+</span>
        <div class="gsr-base__item">
          <span class="gsr-kpi__label">
            New Signups
            <app-info-tip label="New Signups" [text]="tips.membership.newSignups"></app-info-tip>
          </span>
          <span class="gsr-base__value gsr-base__value--up">
            {{ report.membership.base.newSignups | number }}
          </span>
        </div>
        <span class="gsr-base__op" aria-hidden="true">&minus;</span>
        <div class="gsr-base__item">
          <!--
            Lapses are cancellations for this equation's purposes: an imported
            member whose plan ran out left, even though nobody wrote a
            cancellation for them. The split is spelled out underneath so the
            number is not mistaken for people who actively cancelled.
          -->
          <span class="gsr-kpi__label">
            Cancellations &amp; Lapses
            <app-info-tip
              label="Cancellations & Lapses"
              [text]="tips.membership.cancellations"
            ></app-info-tip>
          </span>
          <span class="gsr-base__value gsr-base__value--down">
            {{ report.membership.base.cancellations | number }}
          </span>
          @if (report.membership.base.lapsed > 0) {
            <span class="gsr-kpi__hint">
              {{ report.membership.base.lapsed | number }} lapsed with no cancellation recorded
            </span>
          }
        </div>
        <span class="gsr-base__op" aria-hidden="true">=</span>
        <div class="gsr-base__item gsr-base__item--result">
          <span class="gsr-kpi__label">
            Ending Members
            <app-info-tip label="Ending Members" [text]="tips.membership.ending"></app-info-tip>
          </span>
          <span class="gsr-base__value">{{ report.membership.base.ending | number }}</span>
          <span class="gsr-kpi__delta gsr-kpi__delta--up">
            Net {{ report.membership.base.netChange >= 0 ? '+' : ''
            }}{{ report.membership.base.netChange | number }}
          </span>
        </div>
      </div>

      <!--
        Ending members by plan. Sits directly under the base equation on purpose:
        the Total row has to read the same as Ending Members immediately above
        it, so a drift is visible without leaving the section.
      -->
      @if (planRows.length > 0) {
        <h4 class="gsr-subtitle">Active members by plan</h4>
        <div class="gsr-table-wrap">
          <table class="gsr-table">
            <thead>
              <tr>
                <th scope="col">Plan</th>
                <th scope="col" class="gsr-num">Active Members</th>
                <th scope="col" class="gsr-num">Customers</th>
                <th scope="col" class="gsr-num">Share</th>
              </tr>
            </thead>
            <tbody>
              @for (row of planRows; track trackByPlanRow($index, row)) {
                <tr>
                  <td class="gsr-strong">{{ row.planName }}</td>
                  <td class="gsr-num">{{ row.activeMembers | number }}</td>
                  <td class="gsr-num">{{ row.activeCustomers | number }}</td>
                  <td class="gsr-num">
                    {{ row.sharePct === null ? '—' : (row.sharePct | number: '1.1-1') + '%' }}
                  </td>
                </tr>
              }
              <tr class="gsr-total-row">
                <td>Total</td>
                <td class="gsr-num">{{ planRowsTotal | number }}</td>
                <td class="gsr-num">{{ report.membership.base.endingCustomers | number }}</td>
                <td class="gsr-num">100.0%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!--
          The parts are grouped from the same population as Ending Members, so
          they cannot disagree unless something upstream changed. If they ever
          do, say so rather than printing a table that quietly contradicts the
          number above it.
        -->
        @if (planRowsTotal !== report.membership.base.ending) {
          <p class="gsr-note gsr-note--scope" role="note">
            These plan counts add to {{ planRowsTotal | number }}, not
            {{ report.membership.base.ending | number }}. Treat the breakdown as incomplete.
          </p>
        }
        @if (planRowsCustomersOverlap) {
          <p class="gsr-note gsr-note--scope" role="note">
            Customers are counted once per plan, so the column adds to more than the total: some
            customers hold more than one plan.
          </p>
        }
      }

      <div class="gsr-kpis gsr-kpis--four">
        <div class="gsr-kpi" [class.gsr-kpi--unavailable]="report.membership.churnPct === null">
          <span class="gsr-kpi__label">
            Churn
            <app-info-tip label="Churn" [text]="tips.membership.churn"></app-info-tip>
          </span>
          <span class="gsr-kpi__value">
            {{
              report.membership.churnPct === null
                ? 'N/A'
                : (report.membership.churnPct | number: '1.1-1') + '%'
            }}
          </span>
          <span class="gsr-kpi__hint">
            {{ report.membership.base.cancellations | number }} cancelled or lapsed &divide;
            {{ report.membership.base.beginning | number }} beginning members
          </span>
        </div>
        <div class="gsr-kpi" [class.gsr-kpi--unavailable]="report.membership.arpm === null">
          <span class="gsr-kpi__label">
            ARPM
            <app-info-tip label="ARPM" [text]="tips.membership.arpm"></app-info-tip>
          </span>
          <span class="gsr-kpi__value">
            {{
              report.membership.arpm === null ? 'N/A' : (report.membership.arpm | currency: 'USD')
            }}
          </span>
          <span class="gsr-kpi__hint">
            @if (report.membership.arpm === null) {
              No billing recorded to divide
            } @else {
              Billed &divide; {{ report.membership.avgActiveMembers | number }} avg members
            }
          </span>
        </div>
        <div
          class="gsr-kpi"
          [class.gsr-kpi--unavailable]="report.membership.avgWashesPerMember === null"
        >
          <span class="gsr-kpi__label">
            Avg Washes / Member
            <app-info-tip
              label="Avg Washes / Member"
              [text]="tips.membership.avgWashesPerMember"
            ></app-info-tip>
          </span>
          <span class="gsr-kpi__value">
            {{
              report.membership.avgWashesPerMember === null
                ? 'N/A'
                : (report.membership.avgWashesPerMember | number: '1.1-1')
            }}
          </span>
          <span class="gsr-kpi__hint">
            {{ report.membership.washesRedeemed | number }} redeemed over
            {{ report.membership.avgWashesPerMemberPeriodDays || report.meta.calendarDays }} days
          </span>
        </div>
        <div
          class="gsr-kpi"
          [class.gsr-kpi--unavailable]="report.membership.captureRatePct === null"
        >
          <span class="gsr-kpi__label">
            Membership Capture Rate
            <app-info-tip
              label="Membership Capture Rate"
              [text]="tips.membership.captureRate"
            ></app-info-tip>
          </span>
          <span class="gsr-kpi__value">
            {{
              report.membership.captureRatePct === null
                ? 'N/A'
                : (report.membership.captureRatePct | number: '1.1-1') + '%'
            }}
          </span>
          <span class="gsr-kpi__hint">
            {{ report.membership.base.newSignups | number }} signups &divide;
            {{ report.membership.eligibleRetailCustomers | number }} eligible
          </span>
        </div>
      </div>

      <div class="gsr-grid-2">
        <div class="gsr-panel">
          <span class="gsr-panel__title">
            Membership Revenue Billed
            <app-info-tip
              label="Membership Revenue Billed"
              [text]="tips.membership.revenueBilled"
            ></app-info-tip>
          </span>
          @if (report.membership.billingAvailable === false) {
            <span class="gsr-base__value" style="color: #a7abb3">N/A</span>
            <span class="gsr-note">
              No membership billing is recorded for this period, so revenue is unknown rather than
              zero.
            </span>
          } @else {
            <span class="gsr-base__value">
              {{ report.membership.revenueBilled | currency: 'USD' : 'symbol' : '1.0-0' }}
            </span>
            <span class="gsr-note">
              Plan purchases plus renewal charges this period, whether or not the member has washed
              yet. Refunds are not deducted; imported memberships billed outside the platform are
              not included.
            </span>
          }
        </div>
        <div class="gsr-panel">
          <span class="gsr-panel__title">
            Member Washes Redeemed
            <app-info-tip
              label="Member Washes Redeemed"
              [text]="tips.membership.washesRedeemed"
            ></app-info-tip>
          </span>
          <span class="gsr-base__value">{{ report.membership.washesRedeemed | number }}</span>
          <span class="gsr-note">
            Service delivered &mdash; a separate measure, never netted against billing.
          </span>
        </div>
      </div>

      <div class="gsr-split gsr-split--charts">
        <div class="gsr-panel">
          <div class="gsr-panel__head">
            <span class="gsr-panel__title">
              Active members
              <app-info-tip
                label="Active members"
                [text]="tips.membership.activeMembers"
              ></app-info-tip>
            </span>
            <div class="gsr-legend">
              <span class="gsr-legend__item">
                <span class="gsr-swatch" [style.background]="seriesViolet"></span>Active members
              </span>
            </div>
          </div>
          @if (memberYTicks.length) {
            <span class="gsr-section__meta">Axis starts at {{ memberYTicks[0].label }}</span>
          }
          <svg
            class="gsr-chart"
            viewBox="0 0 800 210"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Active members across the selected period"
          >
            @for (tick of memberYTicks; track tick.value) {
              <line class="gsr-grid" x1="56" [attr.y1]="tick.y" x2="760" [attr.y2]="tick.y" />
              <text class="gsr-axis" x="46" [attr.y]="tick.y + 4" text-anchor="end">
                {{ tick.label }}
              </text>
            }
            <polyline
              [attr.points]="memberPoints"
              fill="none"
              [attr.stroke]="seriesViolet"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              [attr.cx]="memberLastX"
              [attr.cy]="memberLastY"
              r="4"
              [attr.fill]="seriesViolet"
              stroke="#FFFFFF"
              stroke-width="2"
            />
            @for (tick of memberXTicks; track tick.x) {
              <text class="gsr-axis" [attr.x]="tick.x" y="200" text-anchor="middle">
                {{ tick.label }}
              </text>
            }
            @for (band of memberBands; track band.cx; let i = $index) {
              <rect
                [attr.x]="band.x"
                y="20"
                [attr.width]="band.width"
                height="160"
                fill="transparent"
                (mouseenter)="onChartHover('members', i)"
                (mouseleave)="onChartLeave()"
              ></rect>
            }
            @if (activeBand('members'); as band) {
              <line
                [attr.x1]="band.cx"
                y1="20"
                [attr.x2]="band.cx"
                y2="180"
                stroke="#A7ABB3"
                stroke-width="1"
              />
              <circle
                [attr.cx]="band.cx"
                [attr.cy]="band.cy"
                r="4"
                [attr.fill]="seriesViolet"
                stroke="#FFFFFF"
                stroke-width="2"
              />
              <g [attr.transform]="'translate(' + tooltipX(band, 170, 760, 56) + ', 26)'">
                <rect
                  width="170"
                  [attr.height]="26 + band.lines.length * 16"
                  rx="6"
                  fill="#15172E"
                  opacity="0.94"
                />
                <text x="12" y="18" fill="#FFFFFF" font-size="11" font-weight="600">
                  {{ band.label }}
                </text>
                @for (line of band.lines; track line.text; let li = $index) {
                  <text
                    x="12"
                    [attr.y]="36 + li * 16"
                    [attr.fill]="line.color || '#D3D5D9'"
                    font-size="11"
                  >
                    {{ line.text }}
                  </text>
                }
              </g>
            }
          </svg>
        </div>

        <div class="gsr-panel">
          <div class="gsr-panel__head">
            <span class="gsr-panel__title">
              Signups vs departures
              <app-info-tip
                label="Signups vs departures"
                [text]="tips.membership.signupsVsDepartures"
              ></app-info-tip>
            </span>
            <div class="gsr-legend">
              <span class="gsr-legend__item">
                <span class="gsr-swatch" [style.background]="seriesGreen"></span>New
              </span>
              <span class="gsr-legend__item">
                <span class="gsr-swatch" [style.background]="seriesAmber"></span>Cancelled or lapsed
              </span>
            </div>
          </div>
          <svg
            class="gsr-chart"
            viewBox="0 0 380 210"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Signups and departures by week"
          >
            @for (tick of flowYTicks; track tick.value) {
              <line class="gsr-grid" x1="34" [attr.y1]="tick.y" x2="370" [attr.y2]="tick.y" />
              <text class="gsr-axis" x="26" [attr.y]="tick.y + 4" text-anchor="end">
                {{ tick.label }}
              </text>
            }
            @for (bar of flowBars; track bar.label) {
              <rect
                [attr.x]="bar.newX"
                [attr.y]="bar.newY"
                [attr.width]="flowBarWidth"
                [attr.height]="bar.newHeight"
                [attr.rx]="flowBarWidth > 6 ? 3 : 1"
                [attr.fill]="seriesGreen"
              >
                <title>
                  {{ bar.label }} · {{ bar.newSignups | number }} new signups ·
                  {{ bar.cancellations | number }} cancelled or lapsed
                </title>
              </rect>
              <rect
                [attr.x]="bar.cancelX"
                [attr.y]="bar.cancelY"
                [attr.width]="flowBarWidth"
                [attr.height]="bar.cancelHeight"
                [attr.rx]="flowBarWidth > 6 ? 3 : 1"
                [attr.fill]="seriesAmber"
              >
                <title>
                  {{ bar.label }} · {{ bar.cancellations | number }} cancelled or lapsed ·
                  {{ bar.newSignups | number }} new signups
                </title>
              </rect>
              @if (bar.showLabel) {
                <text class="gsr-axis" [attr.x]="bar.labelX" y="200" text-anchor="middle">
                  {{ bar.label }}
                </text>
              }
            }
          </svg>
        </div>
      </div>
    }
  </section>

  <div class="gsr-divider"></div>

  <!-- Prepaid wash books -->
  <section class="gsr-section" aria-labelledby="gsr-prepaid-title">
    <div class="gsr-section__head">
      <h3 id="gsr-prepaid-title" class="gsr-section__title">Prepaid Wash Books</h3>
      <span class="gsr-section__meta">
        Prepaid wash books sold &middot;
        {{ report.prepaid.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}
      </span>
    </div>

    <div class="gsr-kpis">
      <div class="gsr-kpi">
        <span class="gsr-kpi__label">
          Prepaid Wash Books Sold
          <app-info-tip
            label="Prepaid Wash Books Sold"
            [text]="tips.prepaid.booksSold"
          ></app-info-tip>
        </span>
        <span class="gsr-kpi__value">{{ report.prepaid.booksSold | number }}</span>
        <span class="gsr-kpi__hint">Purchased this period</span>
      </div>
      <div class="gsr-kpi">
        <span class="gsr-kpi__label">
          Prepaid Washes Purchased
          <app-info-tip
            label="Prepaid Washes Purchased"
            [text]="tips.prepaid.washesPurchased"
          ></app-info-tip>
        </span>
        <span class="gsr-kpi__value">{{ report.prepaid.washesPurchased | number }}</span>
        <span class="gsr-kpi__hint">Washes those books carry</span>
      </div>
      <div class="gsr-kpi">
        <span class="gsr-kpi__label">
          Prepaid Wash Book Revenue
          <app-info-tip
            label="Prepaid Wash Book Revenue"
            [text]="tips.prepaid.revenue"
          ></app-info-tip>
        </span>
        <span class="gsr-kpi__value">
          {{ report.prepaid.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}
        </span>
        <span class="gsr-kpi__hint">Deferred &mdash; not counted as wash revenue</span>
      </div>
      <div class="gsr-kpi">
        <span class="gsr-kpi__label">
          Washes Redeemed
          <app-info-tip label="Washes Redeemed" [text]="tips.prepaid.washesRedeemed"></app-info-tip>
        </span>
        <span class="gsr-kpi__value">{{ report.prepaid.washesRedeemed | number }}</span>
        <span class="gsr-kpi__hint">Washed at $0 &mdash; already paid for</span>
      </div>
    </div>

    <p class="gsr-note">
      Prepaid wash books sold and washes redeemed are different groups: a wash redeemed now usually
      came from a prepaid wash book sold in an earlier period, so the two are not a redemption rate.
    </p>

    @if (report.prepaid.rows.length) {
      <div class="gsr-table-wrap">
        <table class="gsr-table">
          <thead>
            <tr>
              <th scope="col">
                Prepaid Wash Book
                <app-info-tip label="Prepaid Wash Book" [text]="tips.prepaid.book"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Prepaid Wash Books Sold
                <app-info-tip
                  label="Prepaid Wash Books Sold"
                  [text]="tips.prepaid.rowBooksSold"
                ></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Washes Purchased
                <app-info-tip
                  label="Washes Purchased"
                  [text]="tips.prepaid.rowWashesPurchased"
                ></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Revenue
                <app-info-tip label="Revenue" [text]="tips.prepaid.rowRevenue"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Washes Redeemed
                <app-info-tip
                  label="Washes Redeemed"
                  [text]="tips.prepaid.rowWashesRedeemed"
                ></app-info-tip>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (row of report.prepaid.rows; track trackByPrepaidBook($index, row)) {
              <tr>
                <td class="gsr-strong">{{ row.bookName }}</td>
                <td class="gsr-num">{{ row.booksSold | number }}</td>
                <td class="gsr-num">{{ row.washesPurchased | number }}</td>
                <td class="gsr-num">{{ row.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}</td>
                <td class="gsr-num">{{ row.washesRedeemed | number }}</td>
              </tr>
            }
            <tr class="gsr-total-row">
              <td>Total</td>
              <td class="gsr-num">{{ report.prepaid.booksSold | number }}</td>
              <td class="gsr-num">{{ report.prepaid.washesPurchased | number }}</td>
              <td class="gsr-num">
                {{ report.prepaid.revenue | currency: 'USD' : 'symbol' : '1.0-0' }}
              </td>
              <td class="gsr-num">{{ report.prepaid.washesRedeemed | number }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    } @else {
      <div class="gsr-table-wrap">
        <table class="gsr-table">
          <tbody>
            <tr>
              <td class="gsr-table__empty">No prepaid activity in this period.</td>
            </tr>
          </tbody>
        </table>
      </div>
    }
  </section>

  <div class="gsr-divider"></div>

  <!-- Revenue leakage -->
  <section class="gsr-section" aria-labelledby="gsr-leakage-title">
    <div class="gsr-leakage-head">
      <div class="gsr-leakage-head__main">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 2.75l7 12.5H3l7-12.5zM10 8v3.25M10 13.5h.01"
            stroke="#15172E"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div>
          <h3 id="gsr-leakage-title" class="gsr-section__title">Revenue Leakage</h3>
          <span class="gsr-section__meta">
            Discounts, rewashes, voids and refunds &mdash; where revenue is being given away.
          </span>
        </div>
      </div>
      <span class="gsr-leakage-head__total">
        {{ report.leakage.totalLeakage | currency: 'USD' : 'symbol' : '1.0-0' }} &middot;
        {{ report.leakage.totalLeakagePctOfGross | number: '1.2-2' }}% of gross
        <app-info-tip label="Total leakage" [text]="tips.leakage.total"></app-info-tip>
      </span>
    </div>

    <div class="gsr-kpis gsr-kpis--four">
      <div class="gsr-kpi">
        <span class="gsr-kpi__label">
          Discounts &amp; Coupons
          <app-info-tip label="Discounts & Coupons" [text]="tips.leakage.discounts"></app-info-tip>
        </span>
        <span class="gsr-kpi__value">
          {{ report.leakage.discountTotalAmount | currency: 'USD' : 'symbol' : '1.0-0' }}
        </span>
        <span class="gsr-kpi__hint">
          {{ report.leakage.discountTotalCount | number }} orders &middot;
          {{ report.leakage.discountPctOfGross | number: '1.2-2' }}% of gross
        </span>
      </div>
      <div class="gsr-kpi">
        <span class="gsr-kpi__label">
          Rewashes
          <app-info-tip label="Rewashes" [text]="tips.leakage.rewashes"></app-info-tip>
        </span>
        <span class="gsr-kpi__value">{{ report.leakage.rewashTotalCount | number }}</span>
        <span class="gsr-kpi__hint">
          {{ report.leakage.rewashTotalImpact | currency: 'USD' : 'symbol' : '1.0-0' }} revenue
          impact
        </span>
      </div>
      @for (row of report.leakage.adjustments; track trackByAdjustment($index, row)) {
        <div class="gsr-kpi">
          <span class="gsr-kpi__label">
            {{ row.label }}
            <app-info-tip [label]="row.label" [text]="tips.leakage[row.category]"></app-info-tip>
          </span>
          <span class="gsr-kpi__value">{{ row.count | number }}</span>
          <span class="gsr-kpi__hint">
            {{ row.amount | currency: 'USD' : 'symbol' : '1.0-0' }} &middot;
            {{ row.pctOfGross | number: '1.2-2' }}% of gross
          </span>
        </div>
      }
    </div>

    <div class="gsr-subsection">
      <h4 class="gsr-subtitle">Discounts &amp; coupons by type</h4>
      <div class="gsr-table-wrap">
        <table class="gsr-table">
          <thead>
            <tr>
              <th scope="col">
                Discount Type
                <app-info-tip
                  label="Discount Type"
                  [text]="tips.leakage.discountType"
                ></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Count
                <app-info-tip label="Count" [text]="tips.leakage.discountCount"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Discount Amount
                <app-info-tip
                  label="Discount Amount"
                  [text]="tips.leakage.discountAmount"
                ></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                % of Gross Sales
                <app-info-tip
                  label="% of Gross Sales"
                  [text]="tips.leakage.pctOfGross"
                ></app-info-tip>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (row of report.leakage.discounts; track trackByDiscount($index, row)) {
              <tr>
                <td class="gsr-strong">{{ row.type }}</td>
                <td class="gsr-num">{{ row.count | number }}</td>
                <td class="gsr-num">{{ row.amount | currency: 'USD' : 'symbol' : '1.0-0' }}</td>
                <td class="gsr-num">{{ row.pctOfGross | number: '1.2-2' }}%</td>
              </tr>
            }
            <tr class="gsr-total-row">
              <td>Total</td>
              <td class="gsr-num">{{ report.leakage.discountTotalCount | number }}</td>
              <td class="gsr-num">
                {{ report.leakage.discountTotalAmount | currency: 'USD' : 'symbol' : '1.0-0' }}
              </td>
              <td class="gsr-num">{{ report.leakage.discountPctOfGross | number: '1.2-2' }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="gsr-grid-2">
      <div class="gsr-subsection">
        <h4 class="gsr-subtitle">Rewashes by reason</h4>
        <div class="gsr-table-wrap">
          <table class="gsr-table">
            <thead>
              <tr>
                <th scope="col">
                  Rewash Reason
                  <app-info-tip
                    label="Rewash Reason"
                    [text]="tips.leakage.rewashReason"
                  ></app-info-tip>
                </th>
                <th scope="col" class="gsr-num">
                  Count
                  <app-info-tip label="Count" [text]="tips.leakage.rewashCount"></app-info-tip>
                </th>
                <th scope="col" class="gsr-num">
                  Impact
                  <app-info-tip label="Impact" [text]="tips.leakage.rewashImpact"></app-info-tip>
                </th>
                <th scope="col" class="gsr-num">
                  % of Rewashes
                  <app-info-tip
                    label="% of Rewashes"
                    [text]="tips.leakage.pctOfRewashes"
                  ></app-info-tip>
                </th>
              </tr>
            </thead>
            <tbody>
              @for (
                row of report.leakage.rewashesByReason;
                track trackByRewashReason($index, row)
              ) {
                <tr>
                  <td class="gsr-strong">{{ row.reason }}</td>
                  <td class="gsr-num">{{ row.count | number }}</td>
                  <td class="gsr-num">
                    {{ row.revenueImpact | currency: 'USD' : 'symbol' : '1.0-0' }}
                  </td>
                  <td class="gsr-num">{{ row.pctOfRewashes | number: '1.1-1' }}%</td>
                </tr>
              } @empty {
                <tr>
                  <td class="gsr-table__empty" colspan="4">No rewashes in this period.</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <div class="gsr-subsection">
        <h4 class="gsr-subtitle">Rewashes by employee</h4>
        @if (report.permissions.canViewEmployeeLeakage) {
          <div class="gsr-table-wrap">
            <table class="gsr-table">
              <thead>
                <tr>
                  <th scope="col">
                    Employee
                    <app-info-tip label="Employee" [text]="tips.leakage.employee"></app-info-tip>
                  </th>
                  <th scope="col" class="gsr-num">
                    Rewash Count
                    <app-info-tip
                      label="Rewash Count"
                      [text]="tips.leakage.rewashCount"
                    ></app-info-tip>
                  </th>
                  <th scope="col" class="gsr-num">
                    Impact
                    <app-info-tip label="Impact" [text]="tips.leakage.rewashImpact"></app-info-tip>
                  </th>
                  <th scope="col" class="gsr-num">
                    % of Rewashes
                    <app-info-tip
                      label="% of Rewashes"
                      [text]="tips.leakage.pctOfRewashes"
                    ></app-info-tip>
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (
                  row of report.leakage.rewashesByEmployee;
                  track trackByRewashEmployee($index, row)
                ) {
                  <tr>
                    <td class="gsr-strong">{{ row.employeeName }}</td>
                    <td class="gsr-num">{{ row.count | number }}</td>
                    <td class="gsr-num">
                      {{ row.revenueImpact | currency: 'USD' : 'symbol' : '1.0-0' }}
                    </td>
                    <td class="gsr-num">{{ row.pctOfRewashes | number: '1.1-1' }}%</td>
                  </tr>
                } @empty {
                  <tr>
                    <td class="gsr-table__empty" colspan="4">No rewashes in this period.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="gsr-unavailable">
            <span class="gsr-unavailable__title">Employee-level detail is restricted.</span>
            <span class="gsr-unavailable__body">
              Your role does not include access to employee-level leakage data.
            </span>
          </div>
        }
      </div>
    </div>

    <div class="gsr-subsection">
      <h4 class="gsr-subtitle">Voids &amp; refunds</h4>
      <div class="gsr-table-wrap">
        <table class="gsr-table">
          <thead>
            <tr>
              <th scope="col">
                Category
                <app-info-tip
                  label="Category"
                  [text]="tips.leakage.adjustmentCategory"
                ></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Count
                <app-info-tip label="Count" [text]="tips.leakage.adjustmentCount"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                Amount
                <app-info-tip label="Amount" [text]="tips.leakage.adjustmentAmount"></app-info-tip>
              </th>
              <th scope="col" class="gsr-num">
                % of Gross
                <app-info-tip label="% of Gross" [text]="tips.leakage.pctOfGross"></app-info-tip>
              </th>
            </tr>
          </thead>
          <tbody>
            @for (row of report.leakage.adjustments; track trackByAdjustment($index, row)) {
              <tr>
                <td class="gsr-strong">{{ row.label }}</td>
                <td class="gsr-num">{{ row.count | number }}</td>
                <td class="gsr-num">{{ row.amount | currency: 'USD' : 'symbol' : '1.0-0' }}</td>
                <td class="gsr-num">{{ row.pctOfGross | number: '1.2-2' }}%</td>
              </tr>
            }
            <tr class="gsr-total-row">
              <td>Total</td>
              <td class="gsr-num">{{ report.leakage.adjustmentTotalCount | number }}</td>
              <td class="gsr-num">
                {{ report.leakage.adjustmentTotalAmount | currency: 'USD' : 'symbol' : '1.0-0' }}
              </td>
              <td class="gsr-num">{{ report.leakage.adjustmentPctOfGross | number: '1.2-2' }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    @if (report.leakage.cancelledOrders?.length) {
      <!-- Collapsed by default: the list can run to hundreds of rows. -->
      <details class="gsr-subsection gsr-collapsible">
        <summary class="gsr-subtitle">
          Cancelled orders ({{ report.leakage.cancelledOrders.length | number
          }}{{ report.leakage.cancelledOrdersTruncated ? '+' : '' }})
        </summary>
        <p class="gsr-note">
          The orders behind the Voids count, and why each was called off. Cancelled washes are held
          out of volume and sales on purpose &mdash; no car was washed &mdash; so this is the only
          record of what happened to them. Automatic cancellations of unpaid checkouts and orders
          cancelled as a test are not voids: they are left out of both the list and the count.
        </p>
        <div class="gsr-table-wrap">
          <table class="gsr-table">
            <thead>
              <tr>
                <th scope="col">
                  Order
                  <app-info-tip label="Order" [text]="tips.leakage.cancelledOrder"></app-info-tip>
                </th>
                <th scope="col">
                  Date
                  <app-info-tip label="Date" [text]="tips.leakage.cancelledDate"></app-info-tip>
                </th>
                <th scope="col">
                  Customer
                  <app-info-tip
                    label="Customer"
                    [text]="tips.leakage.cancelledCustomer"
                  ></app-info-tip>
                </th>
                <th scope="col">
                  Item
                  <app-info-tip label="Item" [text]="tips.leakage.cancelledItem"></app-info-tip>
                </th>
                <th scope="col">
                  Reason
                  <app-info-tip label="Reason" [text]="tips.leakage.cancelledReason"></app-info-tip>
                </th>
                <th scope="col">
                  Cancelled by
                  <app-info-tip
                    label="Cancelled by"
                    [text]="tips.leakage.cancelledBy"
                  ></app-info-tip>
                </th>
                <th scope="col">
                  Refund
                  <app-info-tip label="Refund" [text]="tips.leakage.cancelledRefund"></app-info-tip>
                </th>
                <th scope="col" class="gsr-num">
                  Amount
                  <app-info-tip label="Amount" [text]="tips.leakage.cancelledAmount"></app-info-tip>
                </th>
              </tr>
            </thead>
            <tbody>
              @for (
                row of report.leakage.cancelledOrders;
                track trackByCancelledOrder($index, row)
              ) {
                <tr>
                  <td class="gsr-strong">{{ row.orderCode || '&mdash;' }}</td>
                  <td>{{ row.date || '&mdash;' }}</td>
                  <td>{{ row.customerName || row.licensePlate || '&mdash;' }}</td>
                  <td>{{ cancelledOrderItem(row) }}</td>
                  <td>{{ row.reason || 'No reason recorded' }}</td>
                  <td>{{ row.cancelledBy || '&mdash;' }}</td>
                  <td>{{ cancelledOrderRefund(row) }}</td>
                  <td class="gsr-num">{{ row.amount | currency: 'USD' : 'symbol' : '1.2-2' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        @if (report.leakage.cancelledOrdersTruncated) {
          <span class="gsr-note">
            Showing the most recent {{ report.leakage.cancelledOrders.length | number }}. The Voids
            count above covers every void in the period.
          </span>
        }
      </details>
    }
  </section>
</div>
